import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { ErrorBoundary } from '@sentry/react'
import { Resizable } from 're-resizable'
import dayjs from 'dayjs'
import { useLocation, useParams } from 'wouter'
import queryString from 'query-string'

import { Image } from '@/components/pdf/Image'
import { useCADPageStore } from '@/state'
import { useDocumentState } from '@/state/document'
import {
  DEFAULT_VIEW_HEIGHT,
  DEFAULT_VIEW_WIDTH,
  MIN_VIEW_HEIGHT,
  MIN_VIEW_WIDTH,
} from '@/pages/CADPage/constants'
import {
  ImageTemplate,
  TemplateAttribute,
  useUpdateTemplateElement,
  ViewTemplate,
} from '@/services/hooks/template_attributes'
import { cn } from '@/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { VerticalDotsIcon } from '@/components/icons/VerticalDotsIcon'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { useDocumentPageParams } from '../hooks'
import { useAnnotationLayer } from './Annotations/queries'
import { useAnnotationsContext } from './Annotations/AnnotationsContext'
import {
  getAbsoluteXFromDocumentPageOrder,
  getAbsoluteYFromSubPage,
} from './Annotations/utils'
import { DocumentPage, View } from '@/lib/api/client'
import { BroadcastIcon } from '@/components/icons/BroadcastIcon'
import {
  getViewQuery,
  useListPageViews,
  useUpdateView,
} from '@/services/queries/views'
import { useRelativeToAbsolutePositioning } from '@/utils/hooks/useRelativeToAbsolutePositioning'
import { OptionsContextMenu } from '@/components/core/OptionsContextMenu'
import { useDocumentPageQuery } from '../queries'
import { useListImages } from '@/services/queries/images'

export enum ImageTypeEnum {
  Image = 'image',
  View = 'view',
}
type DraggableImageProps = {
  template: TemplateAttribute<ImageTemplate | ViewTemplate>
  isReadOnly?: boolean
  documentPage: DocumentPage
  dataType: ImageTypeEnum
}
export const DraggableImage = ({
  template,
  isReadOnly,
  documentPage,
  dataType,
}: DraggableImageProps) => {
  const INITIAL_DELTA = useMemo(() => ({ x: 0, y: 0 }), [])
  const onLoad = useDocumentState((state) => state.onImageLoaded)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const zoom = useDocumentState((state) => state.zoom)
  const zoomRatio = useMemo(() => zoom / 100, [zoom])
  const isAbsolute = useMemo(
    () => template.template_values.imagePosition === 'absolute',
    [template.template_values.imagePosition],
  )
  const imageId = useMemo(
    () => template?.template_values.imageId || template?.template_values.viewId,
    [template],
  )
  const documentPageId = useMemo(
    () => documentPage.id as string,
    [documentPage],
  )
  const selectElement = useDocumentState((state) => state.setSelectedElement)
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const isSelected = useMemo(
    () =>
      selectedElement?.documentPageId === documentPageId &&
      (selectedElement?.id === template?.id ||
        (imageId && selectedElement?.id === imageId)),
    [selectedElement, documentPageId, template?.id, imageId],
  )
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isHoveringImage, setIsHoveringImage] = useState(false)
  const {
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    isLoading: isOptionsMenuLoading,
  } = useAnnotationLayer(documentPageId)
  const { data } = useDocumentPageQuery()
  const { data: views } = useListPageViews({
    cadVersionId: data?.version?.id,
    documentVersionId: documentPage.document_version,
    enabled: dataType === ImageTypeEnum.View,
  })
  const { data: images } = useListImages({
    documentVersionId: documentPage.document_version,
    enabled: dataType === ImageTypeEnum.Image,
  })
  const imageData = useMemo(() => {
    if (dataType === ImageTypeEnum.Image) {
      return images?.find((image) => image.id === imageId)
    }
    return views?.find((view) => view.id === imageId)
  }, [images, views, dataType, imageId])

  const userFacingDownloadUrl = useMemo(
    () => `${imageData?.name}.jpg`,
    [imageData],
  )
  const downloadUrl = useMemo(() => {
    const baseUrl = window.location.origin

    const placeholderImage =
      dataType === ImageTypeEnum.Image
        ? `${baseUrl}/static/images/upload-image-placeholder.png`
        : `${baseUrl}/static/images/cad-placeholder.png`

    return imageData?.download_url || placeholderImage
  }, [imageData, dataType])
  const timestamp = useMemo(() => {
    if (dataType === ImageTypeEnum.View) {
      return (imageData as View)?.created
    }
  }, [imageData])
  const isDynamic = useMemo(() => (imageData as View)?.dynamic, [imageData])
  const documentPageOrder = useMemo(
    () => (imageData as View)?.document_page_order,
    [imageData],
  )
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const {
    isOutOfBounds,
    setIsOutOfBounds,
    setIsResizing,
    annotationsCanvasRef,
  } = useAnnotationsContext()
  const [{ position, delta }, setPosition] = useState({
    position: {
      x: 0,
      y: 0,
    },
    delta: INITIAL_DELTA,
  })
  const [hasLoadedInitialPosition, setHasLoadedInitialPosition] =
    useState(false)
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  })
  const [reset, setReset] = useState({
    width: 0,
    height: 0,
    position: {
      x: 0,
      y: 0,
    },
    delta: { x: 0, y: 0 },
  })
  const positionData = useMemo(
    () => template?.template_values?.position,
    [template],
  )
  const imageSize = useMemo(() => template?.template_values?.size, [template])
  const imagePosition = useMemo(
    () => template?.template_values?.imagePosition || 'relative',
    [template],
  )
  const subPageId = useMemo(
    () => template?.template_values?.subPageId || documentPageId,
    [template],
  )
  const documentSubPages = useMemo(
    () =>
      (documentPage?.template_values?.subPages as Array<string>) ?? [
        documentPage?.id,
      ],
    [documentPage],
  )
  const { mutateAsync: updateTemplate } = useUpdateTemplateElement({
    documentPageId,
  })

  const { attributes, isDragging, listeners, transform, setNodeRef } =
    useDraggable({
      disabled: isAddingElement || isReadOnly || isMenuOpen,
      id: template?.id as string,
      data: { type: dataType },
    })

  const updateImageDimensionsHandler = useCallback(
    async (
      size: { width: number; height: number },
      position: { x: number; y: number },
    ) => {
      await updateTemplate({
        payload: {
          size,
          position,
        },
        data_type: dataType,
        templateAttributeId: template?.id as string,
      })
    },
    [dataType, template?.id, updateTemplate],
  )

  const { setDraggableAnnotationParentRef } = useRelativeToAbsolutePositioning()

  useEffect(() => {
    if (!imageSize) return
    setContainerDimensions(imageSize)
  }, [imageSize])

  useEffect(() => {
    if (!(transform?.x || transform?.y)) return
    setPosition((prevPosition) => ({
      ...prevPosition,
      delta: {
        x: (transform?.x || 0) / zoomRatio,
        y: (transform?.y || 0) / zoomRatio,
      },
    }))
  }, [transform, zoomRatio])

  useEffect(() => {
    if (!positionData) return
    setPosition({
      position: positionData,
      delta: INITIAL_DELTA,
    })
  }, [INITIAL_DELTA, positionData])

  useEffect(() => {
    if (template?.template_values?.position === position) {
      setHasLoadedInitialPosition(true)
    }
  }, [template, position])

  const shouldShowImageMenu = useMemo(
    () =>
      imageData?.download_url &&
      !isReadOnly &&
      (isSelected || isHoveringImage || isMenuOpen),
    [
      isSelected,
      isHoveringImage,
      isMenuOpen,
      isReadOnly,
      imageData?.download_url,
    ],
  )

  if (
    template?.template_values?.position !== position &&
    !hasLoadedInitialPosition
  ) {
    return <></>
  }

  return (
    <OptionsContextMenu
      isReadonly={isReadOnly}
      isDisabled={isOptionsMenuLoading}
      optionsGroups={[
        {
          options: [
            {
              label: 'Bring to front',
              action: () => bringToFront(template?.id as string),
            },
            {
              label: 'Bring forward',
              action: () => bringForward(template?.id as string),
            },
            {
              label: 'Send backward',
              action: () => sendBackward(template?.id as string),
            },
            {
              label: 'Send to back',
              action: () => sendToBack(template?.id as string),
            },
          ],
        },
      ]}
    >
      <div
        data-testid="draggable-image-container"
        className="flex flex-1 p-px box-content absolute"
        ref={containerRef}
        style={{
          minHeight: 180,
          minWidth: 320,
        }}
      />
      <div
        {...attributes}
        {...listeners}
        onMouseEnter={() => setIsHoveringImage(true)}
        onMouseLeave={() => setIsHoveringImage(false)}
        className={cn('border box-content absolute', {
          'cursor-move': !isReadOnly,
          'cursor-default': isReadOnly,
          'shadow-md': isDragging,
          'shadow-gray-200': isDragging,
          'bg-sky-200/10': isDragging,
          'z-[100]': isDragging,
        })}
        style={{
          translate: `${delta?.x || 0}px ${delta?.y || 0}px`,
          top: getAbsoluteYFromSubPage({
            subPageId,
            relativeY: position?.y || 0,
            subPageIds: documentSubPages,
            isRelative: !isAbsolute,
            relativeOffset: 132,
          }),
          left: getAbsoluteXFromDocumentPageOrder({
            relativeX: position?.x || 0,
            isRelative: !isAbsolute,
            documentPageOrder,
            relativeOffset: 350,
          }),
          zIndex: positionData?.z || 1000,
          borderColor: isSelected ? '#818cf8' : 'transparent',
          pointerEvents: 'all',
        }}
        ref={(el) => {
          if (template.id) {
            setDraggableAnnotationParentRef(template.id, el)
          }
        }}
      >
        <Resizable
          minWidth={MIN_VIEW_WIDTH}
          minHeight={MIN_VIEW_HEIGHT}
          maxWidth={612}
          defaultSize={{
            width: DEFAULT_VIEW_WIDTH,
            height: DEFAULT_VIEW_HEIGHT,
          }}
          lockAspectRatio
          className={cn({
            'z-[100]': isDragging,
          })}
          size={
            containerDimensions.width > 0 && containerDimensions.height > 0
              ? containerDimensions
              : undefined
          }
          onResizeStart={(event, _, ref) => {
            event.stopPropagation()
            const width = ref.clientWidth
            const height = ref.clientHeight
            if (setIsResizing) setIsResizing(true)
            setContainerDimensions({ width, height })
            setReset({ width, height, position, delta })
          }}
          onResize={(event, direction, ref) => {
            event.stopPropagation()

            const canvasRect =
              annotationsCanvasRef?.current?.getBoundingClientRect()
            const elementRect = ref.getBoundingClientRect()

            if (!canvasRect) return

            if (
              elementRect.right > canvasRect?.right ||
              elementRect.top < canvasRect?.top ||
              elementRect.left < canvasRect?.left ||
              elementRect.bottom > canvasRect?.bottom
            ) {
              setIsOutOfBounds(true)
              return
            } else if (isOutOfBounds) {
              setIsOutOfBounds(false)
            }

            const newWidth = ref.clientWidth
            const newHeight = ref.clientHeight
            const widthDiff = newWidth - containerDimensions.width
            const heightDiff = newHeight - containerDimensions.height

            const deltaUpdates = {
              topLeft: { x: -widthDiff, y: -heightDiff },
              bottomLeft: { x: -widthDiff, y: 0 },
              topRight: { x: 0, y: -heightDiff },
              bottomRight: { x: 0, y: 0 },
            }

            setContainerDimensions({
              width: newWidth,
              height: newHeight,
            })

            setPosition((prevPosition) => ({
              ...prevPosition,
              delta: {
                x: prevPosition.delta.x + (deltaUpdates[direction]?.x || 0),
                y: prevPosition.delta.y + (deltaUpdates[direction]?.y || 0),
              },
            }))
          }}
          onResizeStop={(event) => {
            event.stopPropagation()
            if (setIsResizing) setIsResizing(false)
            if (imgRef.current && !imgRef.current.hasAttribute('data-loaded'))
              return

            if (isOutOfBounds) {
              const { position, delta, width, height } = reset
              setContainerDimensions({ width, height })
              setPosition({ position, delta })
              setIsOutOfBounds(false)
              return
            }

            if (
              containerDimensions.width < MIN_VIEW_WIDTH ||
              containerDimensions.height < MIN_VIEW_HEIGHT
            )
              return

            updateImageDimensionsHandler(
              {
                width: containerDimensions.width,
                height: containerDimensions.height,
              },
              {
                x: (position?.x || 0) + delta.x,
                y: (position?.y || 0) + delta.y,
              },
            )
              .then(() => {
                selectElement({
                  documentPageId,
                  id: template?.id as string,
                  isRemovable: true,
                })
              })
              .catch((error) => {
                console.error('Error updating image size:', error)
              })
          }}
          handleClasses={{
            topRight: !isReadOnly && isSelected ? 'resize-handle' : '',
            topLeft: !isReadOnly && isSelected ? 'resize-handle' : '',
            bottomRight: !isReadOnly && isSelected ? 'resize-handle' : '',
            bottomLeft: !isReadOnly && isSelected ? 'resize-handle' : '',
          }}
          enable={{
            topRight: true,
            topLeft: true,
            bottomRight: true,
            bottomLeft: true,
            top: false,
            right: false,
            bottom: false,
            left: false,
          }}
        >
          {shouldShowImageMenu && (
            <>
              <div className="absolute top-2 right-2 z-50">
                <ImageMenu
                  documentPageId={documentPageId}
                  downloadUrl={downloadUrl}
                  onMenuOpenChange={setIsMenuOpen}
                  userFacingDownloadUrl={userFacingDownloadUrl}
                  imageId={imageId}
                  type={dataType}
                  isDynamic={isDynamic}
                />
              </div>
              {isDynamic && (
                <BroadcastIcon
                  color="#238636"
                  className="absolute top-2 left-2 z-50 opacity-50"
                />
              )}
            </>
          )}
          <div
            ref={setNodeRef}
            className={cn(
              'flex flex-1 flex-col justify-center items-center h-full w-full',
              {
                'pointer-events-none': isAddingElement || isDragging,
                'pointer-events-auto': !isAddingElement && !isDragging,
              },
            )}
          >
            <div
              className={cn('h-full', {
                'h-full': imagePosition === 'relative',
              })}
            >
              <ErrorBoundary fallback={<div>Failed to load image</div>}>
                <div
                  className={cn(
                    'h-full cursor-move flex justify-center flex-col items-center',
                    {
                      'cursor-move': !isReadOnly,
                      'cursor-default': isReadOnly,
                      'pointer-events-none': isDragging,
                    },
                  )}
                >
                  <Image
                    src={downloadUrl}
                    className={cn('min-h-full h-auto w-auto', {
                      'cursor-move': !isReadOnly,
                      'cursor-default': isReadOnly,
                    })}
                    data-testid="draggable-image"
                    onLoad={(e) => {
                      e.stopPropagation()
                      imgRef.current = e.currentTarget

                      const setLoaded = () => {
                        if (imgRef.current) {
                          imgRef.current.setAttribute('data-loaded', 'true')
                        }
                        onLoad()
                      }

                      if (containerRef.current) {
                        const containerWidth = imageSize
                          ? imageSize.width
                          : containerRef.current.clientWidth ||
                            DEFAULT_VIEW_WIDTH
                        const containerHeight = imageSize
                          ? imageSize.height
                          : containerRef.current.clientHeight ||
                            DEFAULT_VIEW_HEIGHT

                        const aspect =
                          e.currentTarget.naturalWidth /
                          e.currentTarget.naturalHeight

                        const height = e.currentTarget.height
                        const width = height * aspect

                        const x = (containerWidth - width) / 2
                        const y = (containerHeight - height) / 2

                        const shouldResize =
                          !isReadOnly &&
                          width > MIN_VIEW_WIDTH &&
                          height > MIN_VIEW_HEIGHT &&
                          containerDimensions.width > MIN_VIEW_WIDTH &&
                          containerDimensions.height > MIN_VIEW_HEIGHT &&
                          (!imageSize ||
                            imageSize.width > width ||
                            imageSize.height > height)

                        if (shouldResize) {
                          const newSize = { width, height }
                          const newPosition = {
                            x: (position?.x || 0) + x,
                            y: (position?.y || 0) + y,
                          }

                          updateImageDimensionsHandler(newSize, newPosition)
                            .then(() => {
                              setLoaded()
                            })
                            .catch((error) => {
                              console.error('Error updating image size:', error)
                              setLoaded()
                            })
                        } else {
                          setLoaded()
                          setContainerDimensions({
                            height,
                            width,
                          })
                        }
                      } else {
                        setLoaded()
                      }
                    }}
                  />
                </div>
              </ErrorBoundary>
            </div>
            {isSelected && timestamp && (
              <div className="text-xs m-0 absolute bottom-0 font-medium text-gray-500">
                {dayjs(timestamp).format('MMM DD, YYYY hh:mm A')}
              </div>
            )}
          </div>
        </Resizable>
      </div>
    </OptionsContextMenu>
  )
}

type ImageMenuProps = {
  documentPageId: string
  downloadUrl: string
  imageId?: string | null
  userFacingDownloadUrl: string
  onMenuOpenChange: (open: boolean) => void
  type: ImageTypeEnum
  isDynamic?: boolean
}
const ImageMenu = ({
  documentPageId,
  downloadUrl,
  imageId,
  userFacingDownloadUrl,
  onMenuOpenChange,
  type,
  isDynamic,
}: ImageMenuProps) => {
  const [, setLocation] = useLocation()
  const { toast } = useToast()
  const { projectId, documentId } = useParams<{
    projectId: string
    documentId: string
  }>()
  const { cv: cadVersionId } = useDocumentPageParams()
  const { mutateAsync: updateView } = useUpdateView()
  const selectElement = useDocumentState((state) => state.setSelectedElement)
  const setCadPageState = useCADPageStore((state) => state.setState)
  const onDownloadHandler = useCallback(async (event: any) => {
    event.stopPropagation()
    event.preventDefault()

    try {
      const response = await fetch(downloadUrl)

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = userFacingDownloadUrl

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Uh oh! Something went wrong.',
        description: `There was a problem downloading the image: ${error}`,
      })
    }
  }, [])

  const cadLink = useMemo(() => {
    return `/p/${projectId}/document/${documentId}/cad?viewId=${imageId}${cadVersionId ? `&cv=${cadVersionId}` : ''}`
  }, [imageId])

  const onOpenInCadHandler = useCallback(
    (event: any) => {
      event.stopPropagation()
      event.preventDefault()
      setLocation(cadLink)
    },
    [cadLink],
  )

  const onCopyUrlHandler = useCallback(
    async (event: any) => {
      event.stopPropagation()
      event.preventDefault()

      try {
        await navigator.clipboard.writeText(
          `${document.location.origin}${cadLink}`,
        )
        toast({
          variant: 'success',
          description: 'Image URL copied to clipboard',
        })
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Uh oh! Something went wrong.',
          description: `There was a problem generating the image URL: ${error}`,
        })
      }
    },
    [cadLink],
  )

  const onMakeStatic = useCallback(
    async (event: any) => {
      event.stopPropagation()
      event.preventDefault()
      if (!imageId) return
      const view = await getViewQuery({ viewId: imageId })
      await updateView({
        viewId: imageId as string,
        values: {
          ...view,
          document_page_id: view.document_page ?? null,
          dynamic: false,
        },
      })
    },
    [imageId, updateView],
  )

  const retakeLocation = useMemo(() => {
    const qs = queryString.stringify({
      cv: cadVersionId,
      page_id: `${documentPageId}`,
    })
    return `/p/${projectId}/document/${documentId}/edit?${qs}`
  }, [cadVersionId, documentPageId, projectId, documentId])

  return (
    <DropdownMenu onOpenChange={onMenuOpenChange}>
      <DropdownMenuTrigger
        className="h-auto px-2 py-1 rounded-md bg-transparent hover:bg-zinc-100"
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        <VerticalDotsIcon className="w-3 h-3 stroke-gray-800" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="p-2 z-[100]">
        {type === ImageTypeEnum.View && (
          <>
            {isDynamic && (
              <DropdownMenuItem className="p-0">
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={onMakeStatic}
                >
                  Make static
                </Button>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="p-0">
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={onOpenInCadHandler}
              >
                Open in CAD
              </Button>
            </DropdownMenuItem>
            {type === ImageTypeEnum.View && (
              <DropdownMenuItem className="p-0">
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={(e) => {
                    if (!imageId) return
                    setCadPageState({
                      retakeLocation,
                      retakeViewId: imageId,
                      isRetakeModeActive: true,
                    })
                    selectElement({
                      documentPageId,
                      id: imageId,
                      isRemovable: true,
                    })
                    onOpenInCadHandler(e)
                  }}
                >
                  Re-take
                </Button>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="p-0">
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={onCopyUrlHandler}
              >
                Copy URL
              </Button>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem className="p-0">
          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={onDownloadHandler}
          >
            Download
          </Button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default DraggableImage
